<script setup lang="ts">
import { getArticleById, type ArticleDTO } from '@/composables/article/article'
import { ref, onMounted, type Ref, computed, watch } from 'vue'
import { titleBar } from '@/composables/menu/title'
import { useRoute } from 'vue-router'
import type { CommonResponse } from '@/apis/api'
import { handleErr } from '@/composables/exception/handler'
import { CODE_OK } from '@/apis/code'
import { isNotEmpty, isEmpty, renderMessage } from '@/utils/util'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { formatFriendlyTime } from '@/utils/time'
import type MenuItem from '@/type/MenuItem'
import { clientType } from '@/apis/api'
import { CLIENT_TYPE_WEB } from '@/apis/api'
import { getShareInfo, showShareViaWechat, navigateToMiniAppShare } from '@/composables/wechat/wechat'
import { cutTextAndEscape, removeHTMLTags } from '@/utils/util'
import { openLoading, closeLoading } from '@/components/business/loading/loading'
import logo from '@/assets/logo_svg.svg'
import { report } from '@/composables/memorize/memorize'
import type { ReportPvCommand } from '@/composables/obituary/obituary'
import { currentLang } from '@/composables/settings/storage'
import { getCurrentUser } from '@/composables/user/users'
import { isInWechat } from '@/utils/device'
import { imageTextShare } from '@/composables/wechat/templatePresets'
import { isMobile } from '@/utils/util'
import { isLogin } from '@/composables/user/users'

const route = useRoute()
const requestURL = useRequestURL()
const articleId = route.params.id as string

const article = useState<ArticleDTO | undefined>(`article-${articleId}`, () => undefined)
const { t } = useI18n()

const contributeDialogVisible = ref(false)
const introText = computed(() => t('article.card_intro'))
const applyTitleBarState = () => {
  titleBar.value.title = article.value?.title || t('article.title_fallback')
  titleBar.value.backable = true
  titleBar.value.hideBack = false
  titleBar.value.transparent = false
  titleBar.value.showMenuCount = 1
  titleBar.value.menus = [{ name: t('article.share_to_friend'), key: 'share_article', icon: 'i-uil:share' } as MenuItem]
  titleBar.value.onMenuClicked = (item, node) => {
    if (item.key === 'share_article') {
      shareArticle()
    }
  }
  titleBar.value.life = null
  titleBar.value.lifeId = null
}

const { data: ssrData } = await useAsyncData(`article-data-${articleId}`, async () => {
  const { data } = await getArticleById(articleId)
  return data.value ? JSON.parse(JSON.stringify(data.value)) : null
})

if (ssrData.value) {
  const resData = ssrData.value as unknown as CommonResponse<ArticleDTO>
  if (resData.code === CODE_OK) {
    article.value = resData.data
  }
}

watch(
  () => article.value?.title,
  () => {
    applyTitleBarState()
  },
  { immediate: true }
)

onMounted(() => {
  if (!article.value) {
    getArticle()
  }
  applyTitleBarState()
  const reportPvCommand: ReportPvCommand = {
    url: route.path,
    userId: isLogin.value ? getCurrentUser().value?.user?.id : null
    //userId:null
  }
  report(reportPvCommand)
})

const getArticle = async () => {
  const { data, error, statusCode } = await getArticleById(articleId)
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<ArticleDTO>>
    if (resData.value.code !== CODE_OK) {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'error'
      })
    } else {
      article.value = resData.value.data
    }
  }
}

const tags = computed(() => {
  const raw = article.value?.tags
  if (!raw) return []
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
})

function cleanUrlParams(url: string): string {
  try {
    const urlObj = new URL(url)
    // 创建新的URL对象，保持原始的协议、主机名和路径
    const cleanUrl = new URL(urlObj.origin + urlObj.pathname)
    cleanUrl.searchParams.set('redirect', 'true')
    return cleanUrl.toString()
  } catch (error) {
    return url // 发生错误时返回原始URL
  }
}

const shareContent = computed(() => {
  return cutText(removeHTMLTags(article.value?.shareContent || ''), 150)
})

const canonicalUrl = computed(() => `${requestURL.origin}${route.path}`)
const pageTitle = computed(() => article.value?.title || t('article.title_fallback'))
const pageDescription = computed(() => shareContent.value || introText.value)
const seoImage = computed(() => article.value?.thumbnailUrl || logo)

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: pageDescription,
  ogDescription: pageDescription,
  ogImage: seoImage,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

const configureWechatShare = async () => {
  let shareImg = article.value?.thumbnailUrl ? article.value?.thumbnailUrl : logo
  const urlObj = new URL(window.location.href)
  urlObj.searchParams.set('share', 'true')
  urlObj.searchParams.set('redirect', 'true')
  const shareUrl = urlObj.toString()
  console.log('配置分享参数!!!!!!:', shareUrl)
  if (isInWechat() && getCurrentUser().value) {
    // 打印所有配置参数
    const shareTitle = article.value?.title
    const shareDesc = article.value?.shareContent
    const shareImgUrl = shareImg
    getShareInfo(shareTitle, shareDesc, shareImgUrl, shareUrl)
      .then((success) => {
        if (success) {
        } else {
        }
      })
      .catch((error) => {})
  }
}

const shareArticle = async () => {
  if (!import.meta.client) {
    return
  }
  if (clientType.value == CLIENT_TYPE_WEB && !inMiniprograme.value) {
    console.log('333')
    await navigator.clipboard.writeText(cleanUrlParams(window.location.href))
    ElMessage.success(t('obituary.copy_success'))
  } else if (inMiniprograme.value) {
    const imageTextShareObj = new imageTextShare(
      article.value?.title,
      article.value?.thumbnailUrl ? article.value?.thumbnailUrl : logo,
      cleanUrlParams(window.location.href),
      t('article.share_article_title'),
      t('article.share_desc'),
      [
        {
          type: '1',
          style: 'h1', // h1/h2/h3/h4
          content: article.value?.title
        },
        {
          type: '2',
          style: 'null',
          content: shareContent.value
        },
        {
          type: '5',
          userAvatarUrl:
            article.value?.authorUser?.avatar?.middleQuality || article.value?.authorUser?.avatar?.url || '',
          userName: article.value.author,
          userRelation: null,
          publishDate: formatFriendlyTime(article?.value.createdAt, currentLang.value)
        }
      ]
    )
    navigateToMiniAppShare(imageTextShareObj)
  } else if (isInWechat() && isMobile) {
    console.log('h5 中，开始配置')
    configureWechatShare()
    showShareViaWechat.value = true
  } else {
    console.log('其他情况')
  }
}
</script>
<template>
  <BaseLayout overflow-y-auto mt-15 md:mt-0 pl-6 pr-5 pb-10>
    <h1 text-9 text-main my-4 pr-1 leading-11 font-bold class="font-serif-tc">
      {{ article?.title }}
    </h1>
    <div flex="~ row" v-if="tags && tags?.length > 0" my-2>
      <span v-for="tag in tags" :key="tag" mr-2 bg-grey px2.5 py0.8 rounded text-main text-sm>{{ tag }}</span>
    </div>
    <div my-2 text-second flex items-center>
      {{ article?.author }}
    </div>

    <Article :modelValue="article?.contentTxt" :maxHeight="192" :enableExpand="false" :enableTheme="false" />

    <div text-third w-full flex justify-between mt-4>
      <div>
        <span>{{ t('article.read_count2', { count: article?.read ?? 0 }) }}</span>
      </div>
      <div>
        {{
          $t('m.creat_by_time', {
            time: formatFriendlyTime(article?.createdAt, currentLang)
          })
        }}
      </div>
    </div>

    <div class="bg-card rounded-xl bg-gray-50" my-10 p-6>
      <div flex="~ row" items-start justify-between gap-3>
        <div flex="~ col" gap-2 min-w-0>
          <div flex flex-row mb-3>
            <div flex flex-col flex-1>
              <div text-xl font-bold text-main align-baseline>{{ t('article.card_brand') }}</div>
              <span class="text-second text-xs mt-0.5">{{ t('article.card_tagline') }}</span>
            </div>
            <img :src="logo" w-6 h-6 mt-1 />
          </div>
          <div flex="~ row" items-start justify-between gap-3>
            <div text-base text-second leading-6 min-w-0 w-full>
              {{ introText }}
            </div>
          </div>
        </div>
      </div>

      <div mt-6 mb-1 flex="~ row" gap-3>
        <button
          type="button"
          class="btn-outline rounded-full px-6 py-2 text-sm w-full md:w-1/4"
          @click="contributeDialogVisible = true">
          {{ t('article.card_contribute') }}
        </button>
        <NuxtLink
          to="/guide/createLifeAndBaseInfo?redirect=true"
          class="btn-outline rounded-full px-6 py-2 text-sm w-full text-center md:w-1/4">
          {{ t('article.card_create_memorial') }}
        </NuxtLink>
      </div>
    </div>

    <el-dialog
      v-model="contributeDialogVisible"
      width="420px"
      style="max-width: 92%"
      :z-index="5000"
      :show-close="false"
      destroy-on-close>
      <div w-full flex flex-col>
        <div relative w-full flex items-center justify-center pb-4>
          <div text-lg font-bold text-main>{{ t('article.contribute_qr_title') }}</div>
          <div
            i-material-symbols-light:close
            absolute
            right-2
            top-0
            p-2
            icon-button
            text-2xl
            text-third
            @click="contributeDialogVisible = false" />
        </div>
        <div flex="~ col" items-center px-6 pb-6>
          <div w-full max-w-68>
            <div class="bg-card-thin" p-2>
              <img
                src="https://lp-file.lifepage.cc/operation/20260430170424493_XdwXvVgr1k.jpg?x-oss-process=image/format,png/resize,h_480,m_lfit"
                w-full
                aspect-square
                object-cover
                rounded-2 />
            </div>
            <div mt-3 text-center text-sm text-third>{{ t('article.contribute_qr_hint') }}</div>
          </div>
        </div>
      </div>
    </el-dialog>
  </BaseLayout>
</template>
